import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FeatherLogOut, FeatherLayout, FeatherClipboard, FeatherUser, FeatherRabbit, FeatherCalendar, FeatherEdit2, FeatherFolder, FeatherHub, FeatherTrendingUp, FeatherChevronLeft, FeatherChevronRight, FeatherMessageSquare, FeatherHome, FeatherUsers, FeatherFileText, FeatherGrid } from '../ui/icons';
import { workPlanApi, userApi } from '../../services/api';
import { getAvailableScreens } from '../../utils/roleUtils';
import config from '../../config/setup';
import NotificationBell from '../SocialWall/NotificationBell';
import './SidebarLayout.css';

export default function SidebarLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [hasActiveWorkPlan, setHasActiveWorkPlan] = useState(false);
  const [hasUnclosedWorkPlans, setHasUnclosedWorkPlans] = useState(false);
  const [hasClosedTodayPlan, setHasClosedTodayPlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [availableScreens, setAvailableScreens] = useState([]);
  const [isMinimized, setIsMinimized] = useState(() => {
    const saved = localStorage.getItem('sidebarMinimized');
    return saved ? JSON.parse(saved) : true;
  });
  const [isMyWorkplaceExpanded, setIsMyWorkplaceExpanded] = useState(() => {
    const saved = localStorage.getItem('myWorkplaceExpanded');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    if (user?.id) {
      checkWorkPlanStatus();
      checkUserRole();
    }
  }, [user?.id]);

  const checkUserRole = async () => {
    try {
      const { role } = await userApi.getRole();
      setUserRole(role);
      const screens = getAvailableScreens(role);
      setAvailableScreens(screens);
    } catch (err) {
      console.error('Error checking user role:', err);
    }
  };

  const checkWorkPlanStatus = async () => {
    try {
      const [todayClosedPlan, activePlan, unclosedPlans] = await Promise.all([
        workPlanApi.getTodayClosed(),
        workPlanApi.getActive(),
        workPlanApi.getUnclosed()
      ]);

      setHasClosedTodayPlan(!!todayClosedPlan);
      setHasActiveWorkPlan(!!activePlan);
      setHasUnclosedWorkPlans(unclosedPlans && unclosedPlans.length > 0);
    } catch (error) {
      console.error('Failed to check work plan status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    const newState = !isMinimized;
    setIsMinimized(newState);
    localStorage.setItem('sidebarMinimized', JSON.stringify(newState));
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(' ')
          .map(part => part[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : '?';
  };

  // Helper function to render navigation items based on available screens
  const renderNavigationItem = (screenId, icon, label, additionalCondition = true) => {
    if (!availableScreens.includes(screenId) || !additionalCondition) {
      return null;
    }

    return (
      <NavLink 
        to={`/${screenId}`}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
      >
        {icon}
        <span className="sidebar-link-text">{label}</span>
      </NavLink>
    );
  };

  // Helper function to render submenu item
  const renderSubmenuItem = (screenId, label, path) => {
    if (!availableScreens.includes(screenId)) {
      return null;
    }

    return (
      <NavLink 
        to={path}
        className={({ isActive }) => `sidebar-submenu-link ${isActive ? 'active' : ''}`}
      >
        <span className="sidebar-link-text">{label}</span>
      </NavLink>
    );
  };

  // Toggle My Workplace submenu
  const toggleMyWorkplace = () => {
    const newState = !isMyWorkplaceExpanded;
    setIsMyWorkplaceExpanded(newState);
    localStorage.setItem('myWorkplaceExpanded', JSON.stringify(newState));
  };

  if (loading) return null;

  return (
    <div className="main-layout">
      {/* Sidebar */}
      <div className={`sidebar ${isMinimized ? 'minimized' : ''}`}>
        <div className="space-y-6">
          <div 
            className="sidebar-title"
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <FeatherRabbit className="w-6 h-6 mb-2" />
            <span>bunny steps</span>
          </div>
          <div className="sidebar-notifications">
            <NotificationBell />
          </div>
          <nav className="flex flex-col space-y-4">
            {/* Finance screens */}
            {renderNavigationItem('finance-attendance', <FeatherCalendar className="w-5 h-5" />, 'Attendance')}
            {renderNavigationItem('finance-payslips', <FeatherFileText className="w-5 h-5" />, 'Payroll')}
            {renderNavigationItem('finance-payout-summary', <FeatherTrendingUp className="w-5 h-5" />, 'Payroll Summary')}
            
            {/* Dashboard - Available for all users */}
            {renderNavigationItem('dashboard', <FeatherLayout className="w-5 h-5" />, 'Dashboard')}
            
            {/* Social Wall - Available for all users */}
            {renderNavigationItem('social-wall', <FeatherUsers className="w-5 h-5" />, 'The Vibe')}
            
            {/* Partner Companies - Available for managers, HR, and partner specialists */}
            {renderNavigationItem('partner-companies', <FeatherTrendingUp className="w-5 h-5" />, 'Partner Companies')}

            {/* Hirelane - Available for managers and HR */}
            {renderNavigationItem('hirelane', <FeatherGrid className="w-5 h-5" />, 'Hirelane')}
            
            {/* Manager-only screens */}
            {renderNavigationItem('thehub', <FeatherHub className="w-5 h-5" />, 'The Hub')}
            {renderNavigationItem('projects', <FeatherFolder className="w-5 h-5" />, 'Projects')}
            {renderNavigationItem('feature-design-studio', <FeatherGrid className="w-5 h-5" />, 'Feature Design Studio')}
            {renderNavigationItem('daily-work-plan-review', <FeatherClipboard className="w-5 h-5" />, 'Daily Review')}
            {renderNavigationItem('time-offs', <FeatherCalendar className="w-5 h-5" />, 'Time-Offs')}
            {/* {renderNavigationItem('task-conversations', <FeatherMessageSquare className="w-5 h-5" />, 'Task Conversations')} */}
            {/* {renderNavigationItem('peter-conversations', <FeatherMessageSquare className="w-5 h-5" />, 'Peter Conversations')} */}
            
            {/* Work Plan - Available for members and partner specialists (when conditions are met) */}
            {renderNavigationItem('work-plan', <FeatherClipboard className="w-5 h-5" />, 'Work Plan', 
              !hasActiveWorkPlan && !hasUnclosedWorkPlans && !hasClosedTodayPlan)}
            
            {/* My Projects - Available for members (non-managers) */}
            {renderNavigationItem('my-projects', <FeatherFolder className="w-5 h-5" />, 'My Projects')}
            
            {/* Scribble - Available for all users */}
            {renderNavigationItem('scribble', <FeatherEdit2 className="w-5 h-5" />, 'Scribble')}
            
            {/* My Workplace - Available for all users */}
            {(availableScreens.includes('holiday-calendar') || availableScreens.includes('leave-management')) && (
              <div className="sidebar-menu-group">
                <button
                  className="sidebar-menu-group-header"
                  onClick={toggleMyWorkplace}
                  disabled={isMinimized}
                >
                  <FeatherHome className="w-5 h-5" />
                  <span className="sidebar-link-text">My Workplace</span>
                  {!isMinimized && (
                    <span className={`sidebar-chevron ${isMyWorkplaceExpanded ? 'expanded' : ''}`}>
                      <FeatherChevronRight className="w-4 h-4" />
                    </span>
                  )}
                </button>
                {!isMinimized && isMyWorkplaceExpanded && (
                  <div className="sidebar-submenu">
                    {renderSubmenuItem('holiday-calendar', 'Holiday Calendar', '/my-workplace/holiday-calendar')}
                    {renderSubmenuItem('leave-management', 'Leave Management', '/my-workplace/leave-management')}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        <div className="sidebar-footer">
          <button 
            className="sidebar-toggle-btn"
            onClick={toggleSidebar}
            title={isMinimized ? 'Expand sidebar' : 'Minimize sidebar'}
          >
            {isMinimized ? <FeatherChevronRight className="w-5 h-5" /> : <FeatherChevronLeft className="w-5 h-5" />}
          </button>
          {/* App Version */}
          <div className="app-version">
            <span className="version-text">v{config.app.version}</span>
          </div>
          
          <div className="user-profile">
            <div className="user-avatar">
              <FeatherUser className="w-6 h-6" />
              <span className="user-initials">{getInitials(user?.displayName)}</span>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName}</span>
              <span className="user-email">{user?.email}</span>
            </div>
          </div>
          
          <button 
            className="sidebar-button"
            onClick={handleLogout}
          >
            <FeatherLogOut className="w-5 h-5" />
            <span className="sidebar-link-text">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`main-content-wrapper ${isMinimized ? 'sidebar-minimized' : ''}`}>
        {children}
      </div>
    </div>
  );
} 